import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiKey, FiHome, FiLogOut, FiUsers, FiSearch, FiFilter, FiCalendar } from 'react-icons/fi';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.users) {
        setUsers(data.users);
        setFilteredUsers(data.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u => 
        u.username?.toLowerCase().includes(query) ||
        u.name?.toLowerCase().includes(query) ||
        u.phone?.includes(query)
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchQuery, roleFilter, users]);

  if (!user || user.role !== 'admin') {
    navigate('/admin/login');
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const getRoleText = (role) => {
    const roles = {
      'student': '学生',
      'admin': '管理员'
    };
    return roles[role] || role;
  };

  const getPackageTypeText = (type) => {
    const types = {
      'basic': '基础版',
      'standard': '标准版',
      'premium': '高级版'
    };
    return types[type] || type;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'student': { bg: '#e3f2fd', text: '#1976d2' },
      'admin': { bg: '#fff3e0', text: '#f57c00' }
    };
    return colors[role] || { bg: '#e8f5e9', text: '#388e3c' };
  };

  const stats = {
    total: users.length,
    students: users.filter(u => u.role === 'student').length,
    admins: users.filter(u => u.role === 'admin').length,
    todayActive: users.filter(u => {
      if (!u.lastLogin) return false;
      const today = new Date();
      const lastLogin = new Date(u.lastLogin);
      return today.toDateString() === lastLogin.toDateString();
    }).length
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', color: '#1a2b4c' }}>
      {/* 导航栏 */}
      <nav style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 1000, 
        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
        backdropFilter: 'blur(10px)',
        padding: '1rem 0',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div className="container" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '0 1.5rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            textDecoration: 'none',
            color: '#1a2b4c'
          }}>
            <img 
              src="/logo.png" 
              alt="123wonder英文科普" 
              style={{ 
                height: '50px', 
                width: 'auto'
              }} 
            />
            <div>
              <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600', color: '#1a2b4c' }}>管理员后台</h1>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#6c757d' }}>用户管理</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button 
              onClick={() => navigate('/admin/manage')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              管理文件
            </button>
            <button 
              onClick={() => navigate('/admin/invitation')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              邀请码管理
            </button>
            <span style={{ color: '#495057', fontSize: '1.1rem' }}>欢迎，{user?.username}</span>
            <button 
              onClick={handleLogout}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#dc3545',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fff5f5';
                e.target.style.borderColor = '#dc3545';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = '#e9ecef';
              }}
            >
              <FiLogOut />
              退出
            </button>
          </div>
        </div>
      </nav>

      <div className="container" style={{ 
        maxWidth: '1600px', 
        margin: '2rem auto', 
        padding: '0 1.5rem'
      }}>
        {/* 返回按钮 */}
        <div style={{ marginBottom: '1.5rem' }}>
          <button 
            onClick={() => navigate('/admin')}
            style={{
              background: 'none',
              border: '1px solid #e9ecef',
              color: '#495057',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FiHome />
            返回管理页面
          </button>
        </div>

        {/* 用户统计 */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          padding: '2rem', 
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          marginBottom: '2rem'
        }}>
          <h2 style={{ 
            margin: '0 0 1.5rem 0', 
            fontSize: '1.5rem', 
            color: '#1a2b4c',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <FiUsers />
            用户统计
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1.5rem'
          }}>
            <div style={{ 
              backgroundColor: '#e3f2fd', 
              borderRadius: '8px', 
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1976d2', marginBottom: '0.5rem' }}>
                {stats.total}
              </div>
              <div style={{ color: '#495057' }}>总用户数</div>
            </div>
            <div style={{ 
              backgroundColor: '#e8f5e9', 
              borderRadius: '8px', 
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#388e3c', marginBottom: '0.5rem' }}>
                {stats.students}
              </div>
              <div style={{ color: '#495057' }}>学生用户</div>
            </div>
            <div style={{ 
              backgroundColor: '#fff3e0', 
              borderRadius: '8px', 
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f57c00', marginBottom: '0.5rem' }}>
                {stats.admins}
              </div>
              <div style={{ color: '#495057' }}>管理员</div>
            </div>
            <div style={{ 
              backgroundColor: '#f3e5f5', 
              borderRadius: '8px', 
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7b1fa2', marginBottom: '0.5rem' }}>
                {stats.todayActive}
              </div>
              <div style={{ color: '#495057' }}>今日活跃</div>
            </div>
          </div>
        </div>

        {/* 用户列表 */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          padding: '2rem', 
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
        }}>
          <h2 style={{ 
            margin: '0 0 1.5rem 0', 
            fontSize: '1.5rem', 
            color: '#1a2b4c',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FiUsers />
              用户列表
            </div>
            <button 
              onClick={() => navigate('/admin/invitation')}
              style={{
                background: '#1976d2',
                border: 'none',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
            >
              <FiKey /> 查看邀请码
            </button>
          </h2>

          {/* 搜索和筛选 */}
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              backgroundColor: '#f8f9fa',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              flex: 1,
              minWidth: '200px'
            }}>
              <FiSearch style={{ color: '#6c757d' }} />
              <input
                type="text"
                placeholder="搜索用户名、手机号或姓名..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  flex: 1,
                  color: '#1a2b4c'
                }}
              />
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              backgroundColor: '#f8f9fa',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <FiFilter style={{ color: '#6c757d' }} />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  color: '#1a2b4c',
                  cursor: 'pointer'
                }}
              >
                <option value="all">所有角色</option>
                <option value="student">学生</option>
                <option value="admin">管理员</option>
              </select>
            </div>
          </div>

          {/* 用户表格 */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{ 
                  backgroundColor: '#f8f9fa', 
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '1rem', 
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>用户名</th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '1rem', 
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>姓名</th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '1rem', 
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>手机号</th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '1rem', 
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>邀请码</th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '1rem', 
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>角色</th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '1rem', 
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>套餐</th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '1rem', 
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>积分</th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '1rem', 
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>注册时间</th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '1rem', 
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>最后登录</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((userItem, index) => {
                  const roleColors = getRoleBadgeColor(userItem.role);
                  return (
                    <tr key={userItem._id || userItem.id || index} style={{ 
                      borderBottom: '1px solid #e9ecef',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{ 
                            width: '36px', 
                            height: '36px', 
                            borderRadius: '50%', 
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {userItem.username?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <span style={{ color: '#1a2b4c', fontWeight: '500' }}>
                            {userItem.username || '-'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem', color: '#495057' }}>
                        {userItem.name || '-'}
                      </td>
                      <td style={{ padding: '1rem', color: '#495057' }}>
                        {userItem.phone || '-'}
                      </td>
                      <td style={{ padding: '1rem', color: '#495057' }}>
                        {userItem.invitationCode || '-'}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          backgroundColor: roleColors.bg,
                          color: roleColors.text,
                          padding: '0.25rem 0.75rem',
                          borderRadius: '999px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {getRoleText(userItem.role)}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#495057' }}>
                        {getPackageTypeText(userItem.packageType)}
                      </td>
                      <td style={{ padding: '1rem', color: '#495057' }}>
                        {userItem.points || 0}
                      </td>
                      <td style={{ padding: '1rem', color: '#495057' }}>
                        {formatDate(userItem.createdAt)}
                      </td>
                      <td style={{ padding: '1rem', color: '#495057' }}>
                        {formatDate(userItem.lastLogin)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem', 
                color: '#6c757d'
              }}>
                没有找到用户
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
